import React, { useEffect, useMemo, useState } from 'react';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';

import ApiKeyRow from './ApiKeyRow';

function ApiKeysTable({ currentUser, keys = [], newKeyId }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const intl = useIntl();

  const [deleteApiKey, deleteResult] = api.endpoints.deleteApiKey.useMutation();
  const { isSuccess, isError } = deleteResult;

  const keyName =
    keys.find((k) => k.id === selectedKey?.id)?.name ||
    intl.formatMessage({
      id: 'ApiKeysTable.apiKeyNameDefault',
      defaultMessage: 'Untitled API Key',
      description: 'Default name for an API key when no name is provided',
    });

  useEffect(() => {
    if (isSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'ApiKeysTable.apiKeyRemoveSuccess',
            defaultMessage: '"{keyName}" API Key successfully removed.',
            description:
              'Success message when API key is removed. Example: keyName = "Production API Key"',
          },
          { keyName },
        ),
      );
      setSelectedKey(null);
    } else if (isError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'ApiKeysTable.apiKeyRemoveError',
            defaultMessage: 'Failed to remove "{keyName}".',
            description:
              'Error message when API key removal fails. Example: keyName = "Production API Key"',
          },
          { keyName },
        ),
      );
    }
  }, [isSuccess, isError]); // eslint-disable-line react-hooks/exhaustive-deps
  const [modal, showModal] = useModal({
    title: intl.formatMessage({
      id: 'ApiKeysTable.deleteApiKeyTitle',
      defaultMessage: 'Delete API Key?',
      description: 'Title for the delete API key confirmation modal',
    }),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'ApiKeysTable.deleteApiKeyButton',
        defaultMessage: 'Delete',
        description: 'Button text to delete an API key | maxLength=20',
      }),
      variant: 'red',
      errorHandler: () => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'ApiKeysTable.deleteApiKeyError',
            defaultMessage: 'Delete API Key failed. Please try again.',
            description: 'Toast message when deleting API key fails',
          }),
        );
      },
      promise: async () => {
        deleteApiKey(selectedKey);
      },
    },
    content: (
      <Text variant="xs-dense">
        <FormattedMessage
          id="ApiKeysTable.deleteApiKeyContent"
          defaultMessage={
            'Are you sure you want to remove <strong>"{keyName}"</strong> API Key? <strong>You won’t be able to undo this action.</strong>'
          }
          description="Content for the delete API key confirmation modal. Example: keyName = 'Production API Key'"
          values={{
            keyName,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </Text>
    ),
  });

  const currentUserActiveKey = keys.find(
    (k) => k.is_enabled && currentUser.id === k.user_id,
  );

  const sortedKeys = useMemo(() => {
    const _keys = [...keys];
    return _keys.sort((a, b) => (a.date_created < b.date_created ? 1 : -1));
  }, [keys]);

  return (
    <FlexLayout role="table" flexDirection="column">
      <FlexLayout
        alignItems="center"
        px={6}
        py={4}
        sx={{ borderTop: 'border' }}
      >
        <Box pl={4} sx={{ flexBasis: '300px', flexGrow: '1' }}>
          <Text color="gray-700" variant="2xs-dense-bold">
            {intl.formatMessage({
              id: 'ApiKeysTable.generatedKeys',
              defaultMessage: 'Generated Keys',
              description: 'Title for the generated API keys column',
            })}
          </Text>
        </Box>
        <Box sx={{ width: '300px' }}>
          <Text color="gray-700" variant="2xs-dense-bold">
            {intl.formatMessage({
              id: 'ApiKeysTable.createdAt',
              defaultMessage: 'Created',
              description: 'Title for the created at column in API keys table',
            })}
          </Text>
        </Box>
        <Box sx={{ width: '300px' }}>
          <Text color="gray-700" variant="2xs-dense-bold">
            {intl.formatMessage({
              id: 'ApiKeysTable.lastAccess',
              defaultMessage: 'Last access',
              description: 'Title for the last access column in API keys table',
            })}
          </Text>
        </Box>
        <Box sx={{ width: '220px' }}>
          <Text color="gray-700" variant="2xs-dense-bold">
            {intl.formatMessage({
              id: 'ApiKeysTable.status',
              defaultMessage: 'Status',
              description: 'Title for the status column in API keys table',
            })}
          </Text>
        </Box>
      </FlexLayout>
      {sortedKeys.map((key) => (
        <ApiKeyRow
          activeKey={currentUserActiveKey}
          disabled={key.user_id !== currentUser.id}
          item={key}
          key={key.id}
          shouldAnimate={newKeyId === key.id}
          removeItem={(item) => {
            setSelectedKey(item);
            showModal();
          }}
        />
      ))}
      {modal}
    </FlexLayout>
  );
}

export default ApiKeysTable;
